// WHERE add/remove planner. Where fragments are joined by `AND`/`OR`
// combinators; the slice contract is to preserve the user's existing
// combinator style by re-emitting `<combinator> <text>` after the last
// fragment when fragments already exist, and to synthesize a fresh
// ` WHERE <text>` clause when none does. The added text is one predicate:
// a multi-part one is parenthesised (`parenthesizeCompound`), and a clause
// whose top level uses the other connector is wrapped first (`planAppendPredicate`).
// Text written after a trailing line comment starts on a new line
// (`outsideLineComments`).
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { BooleanFragment, PlannedByteEdit, StructuralEdit, WhereCombinator } from '../types';

import type { EditCtx } from './applyStructuralEdit';
import {
  CLAUSE_BOUNDARIES_AFTER_WHERE,
  deleteEdit,
  findClauseInsertionPoint,
  findKeyword,
  insertEdit,
  pickClauseInsertOffset,
  scanStatementKeywords,
} from './applyStructuralEdit.shared';
import { outsideLineComments, parenthesizeCompound } from './wholePredicate';

export function planWhereEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-where' | 'remove-where' | 'clear-where' }>,
): readonly PlannedByteEdit[] {
  if (edit.op === 'add-where') return planAddWhere(ctx, edit);
  if (edit.op === 'clear-where') return planClearWhere(ctx);
  return planRemoveWhere(ctx, edit);
}

/**
 * "Clear all" (report G6) — drop the ENTIRE WHERE clause (every fragment +
 * the leading keyword) as one planned edit, so the whole clause disappears in
 * a single Monaco undo stop regardless of how many fragments it holds. A
 * no-op ([]) when there's no WHERE clause to clear.
 */
function planClearWhere(ctx: EditCtx): readonly PlannedByteEdit[] {
  const where = ctx.structure.where ?? [];
  if (where.length === 0) return [];
  const kws = scanStatementKeywords(ctx);
  const whereKw = findKeyword(kws, 'WHERE');
  const start = whereKw ? whereKw.start : where[0].range.startOffset;
  const end = where[where.length - 1].range.endOffset;
  return [deleteEdit(ctx, start, end, 'clear-where')];
}

function planAddWhere(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-where' }>,
): readonly PlannedByteEdit[] {
  const where = ctx.structure.where ?? [];
  const text = parenthesizeCompound(edit.text, ctx.options);
  if (where.length > 0) {
    return planAppendPredicate(ctx, where, edit.combinator, text, 'add-where');
  }
  // No WHERE clause yet — synthesize one. Anchor to the end of FROM/JOIN
  // (or end of select-list when there's no FROM); insert ` WHERE <text>`
  // before the next clause-boundary keyword.
  const after = whereAnchor(ctx);
  const kws = scanStatementKeywords(ctx);
  const insertAt = findClauseInsertionPoint(
    kws, after, CLAUSE_BOUNDARIES_AFTER_WHERE, ctx.statementInfo.range.endOffset,
  );
  const trimmedAt = pickClauseInsertOffset(
    ctx.source,
    Math.min(after, mysqlDmlTailStart(ctx, kws)),
    insertAt,
    ctx.statementInfo.range.startOffset,
  );
  return [insertEdit(ctx, trimmedAt, outsideLineComments(ctx, trimmedAt, trimmedAt, ` WHERE ${text}`), 'add-where:empty')];
}

/**
 * Append `<AND|OR> <text>` after a clause's last fragment. When the clause's
 * top level is joined by the OTHER connector (`a = 1 OR b = 2` + AND), the
 * existing clause is wrapped first — `(a = 1 OR b = 2) AND c = 3` — so the new
 * condition applies to the whole clause, as the add picker's "Join with AND /
 * OR" says, instead of binding to its last member by precedence. The top-level
 * connector is OR when any top-level fragment is OR-joined (OR splits first),
 * else AND — read off the flat fragments, which UPDATE / DELETE have too. A
 * single predicate or an already-parenthesised clause (one fragment) is
 * appended to as-is. Shared by the HAVING planner. `text` is already one
 * predicate (`parenthesizeCompound`). A clause ending in a line comment
 * (`b = 2 -- note`) is closed / continued on the next line.
 */
export function planAppendPredicate(
  ctx: EditCtx,
  fragments: readonly BooleanFragment[],
  combinator: WhereCombinator,
  text: string,
  label: string,
): readonly PlannedByteEdit[] {
  const end = fragments[fragments.length - 1].range.endOffset;
  const tail = ` ${combinator} ${text}`;
  const topConnector = fragments.some((f) => f.leadingConnector === 'OR') ? 'OR' : 'AND';
  if (fragments.length > 1 && topConnector !== combinator) {
    return [
      insertEdit(ctx, fragments[0].range.startOffset, '(', `${label}:wrap`),
      insertEdit(ctx, end, outsideLineComments(ctx, end, end, `)${tail}`), `${label}:append`),
    ];
  }
  return [insertEdit(ctx, end, outsideLineComments(ctx, end, end, tail), `${label}:append`)];
}

function planRemoveWhere(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'remove-where' }>,
): readonly PlannedByteEdit[] {
  const where = ctx.structure.where ?? [];
  if (edit.fragmentIndex < 0 || edit.fragmentIndex >= where.length) return [];
  if (where.length === 1) {
    // Remove the only fragment AND the leading WHERE keyword. Find WHERE.
    const kws = scanStatementKeywords(ctx);
    const whereKw = findKeyword(kws, 'WHERE');
    if (whereKw) {
      return [deleteEdit(
        ctx, whereKw.start, where[0].range.endOffset, 'remove-where:with-keyword',
      )];
    }
    return [deleteEdit(
      ctx, where[0].range.startOffset, where[0].range.endOffset, 'remove-where:fragment-only',
    )];
  }
  // Multiple fragments — drop one, with its adjacent AND/OR.
  const idx = edit.fragmentIndex;
  let start: number; let end: number;
  if (idx === 0) {
    // Drop fragment[0] + the AND/OR before fragment[1].
    start = where[0].range.startOffset;
    end = where[1].range.startOffset;
  } else {
    // Drop the AND/OR before fragment[idx] + fragment[idx].
    start = where[idx - 1].range.endOffset;
    end = where[idx].range.endOffset;
  }
  return [deleteEdit(ctx, start, end, 'remove-where')];
}

// MySQL's single-table UPDATE / DELETE take `ORDER BY … LIMIT …` AFTER the
// WHERE, and the structure reader can fold that tail into the last SET value
// or read `ORDER` as the target's alias — so the WHERE goes before the first
// top-level ORDER / LIMIT past the target. MySQL only: on the other engines
// LIMIT is a legal bare identifier and their UPDATE / DELETE has no such tail.
function mysqlDmlTailStart(
  ctx: EditCtx,
  kws: ReturnType<typeof scanStatementKeywords>,
): number {
  const { structure } = ctx;
  const target =
    structure.kind === 'update' ? structure.updateTarget
      : structure.kind === 'delete' ? structure.deleteTarget
        : undefined;
  if (ctx.options.engine !== 'mysql' || !target) return Number.POSITIVE_INFINITY;
  const tail = kws.find(
    (k) => k.start >= target.range.startOffset && (k.upper === 'ORDER' || k.upper === 'LIMIT'),
  );
  return tail ? tail.start : Number.POSITIVE_INFINITY;
}

// Find the byte offset that's the natural anchor for a synthesized WHERE
// clause: end of the last JOIN, or end of the last FROM item, or end of the
// last select-list item. An UPDATE / DELETE anchors after whichever of its
// target, SET list and FROM / USING / JOIN tail ends last (MySQL's JOIN comes
// before SET, PostgreSQL / SQL Server's FROM after it) — without this a
// WHERE-less `UPDATE t SET …` / `DELETE FROM t` fell through to the statement
// start and got its WHERE written before the verb, on every engine.
function whereAnchor(ctx: EditCtx): number {
  const { structure } = ctx;
  if (structure.kind === 'update' || structure.kind === 'delete') {
    const ends = [
      structure.updateTarget,
      structure.deleteTarget,
      ...(structure.updateAssignments ?? []),
      ...(structure.fromItems ?? []),
      ...(structure.joins ?? []),
    ].flatMap((item) => (item ? [item.range.endOffset] : []));
    if (ends.length > 0) return Math.max(...ends);
  }
  if (structure.joins?.length) {
    return structure.joins[structure.joins.length - 1].range.endOffset;
  }
  if (structure.fromItems?.length) {
    return structure.fromItems[structure.fromItems.length - 1].range.endOffset;
  }
  if (structure.selectItems?.length) {
    return structure.selectItems[structure.selectItems.length - 1].range.endOffset;
  }
  return ctx.statementInfo.range.startOffset;
}
